import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import { push } from 'react-router-redux'
import styled from 'styled-components'
import withApiData from '../../HOC/withApiData'
import PublishLabel from '../../mini/MyCourses/PublishLabel'
import { showModal } from '../../../actions/modal'
import { convertToRomanNumerals } from '../../../utils/convertToRomanNumerals'
import _SortOrderIcon from '../../../static/images/mycourses/SortOrder.svg'
import EditOrDeleteMenuButton from '../../mini/Buttons/EditOrDeleteMenuButton'
import P2 from '../../typography/paragraphs/P2'
import H5 from '../../typography/headers/H5'
import { ItemTypes } from './ItemTypes'
import {
  deleteActivityCourseJourney,
  reorderActivityCourseJourney
} from '../Journeys/API'
import { cleanHexCode } from '../util'
import FixedAlignmentBox from '../../mini/Layout/FixedAlignmentBox'

const JourneyContainer = styled.div`
  margin: 11px 0;
  background: none;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  align-items: center;
`

const JourneyInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0 10px 22px;
  margin: 0 2.5%;
  border: 1px solid #dedfe0;
  border-radius: 4px;
  background-color: #ffffff;
  flex: 1;
  align-content: center;
  align-items: center;
`

const SortIcon = styled.img.attrs({
  src: _SortOrderIcon
})`
  width: 19px;
  height: 9px;
`

const JourneyRow = ({
  apiData,
  activityCourse,
  index,
  journey,
  moveCard,
  id,
  selectedAcademicYearId
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const gradeLevels = useSelector(state => state.gradelevels.items)
  const gradeLevel = gradeLevels.find(
    gl => gl.id === activityCourse.grade_level_id
  )

  const ref = useRef(null)
  const [, drop] = useDrop({
    accept: ItemTypes.JOURNEY_ROW,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect()

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      const clientOffset = monitor.getClientOffset()

      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
    drop(item) {
      const index = item.index - 1 === -1 ? 0 : item.index
      reorderActivityCourseJourney(apiData, activityCourseId, journey.id, index)
    }
  })

  const [, drag] = useDrag({
    type: ItemTypes.JOURNEY_ROW,
    item: () => {
      return { id, index }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  })

  drag(drop(ref))

  const activityCourseId = activityCourse.id

  const deleteMutation = useMutation(
    ({ apiData, activityCourseId, journeyId }) =>
      deleteActivityCourseJourney(apiData, activityCourseId, journeyId)
  )

  const onDeleteClick = (apiData, activityCourseId, journeyId) => {
    deleteMutation.mutate(
      {
        apiData,
        activityCourseId,
        journeyId: journeyId
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(
            'activity-course-journeys-' + activityCourseId
          )
          queryClient.invalidateQueries(
            'mentor-master-courses-' + selectedAcademicYearId
          )
          queryClient.invalidateQueries(
            'cloneable-journeys-' + selectedAcademicYearId
          )
        }
      }
    )
  }

  const titleInfo = {
    color: activityCourse.color,
    name: activityCourse.name,
    abbreviation: activityCourse.abbreviation,
    journeyName: journey.name,
    journeyIndex: journey.master_course_course.index + 1
  }

  const handleClick = e => {
    dispatch(
      push(`/mycourses/${activityCourse.id}/journey/${journey.id}`)
    )
    e.stopPropagation()
  }

  const handleClose = option => {
    if (option === 'Delete') {
      if (journey.marked_ready_for_enrollment_at === null) {
        dispatch(
          showModal('CLOSE_MODAL', {
            activityCourse: titleInfo,
            gradeLevel: gradeLevel,
            description: 'Are you sure you want to delete this journey?',
            supportiveText: 'This action cannot be undone.',
            isOptionalTextExist: true,
            optionalText: `All${
              journey.quests_count > 0 ? ' ' + journey.quests_count : ''
            } activities will be permanently deleted.`,
            deleteFunction: () =>
              onDeleteClick(apiData, activityCourse.id, journey.id)
          })
        )
      } else {
        // TODO: fix what is displayed here
        dispatch(
          showModal('INFORMATIVE_MODAL', {
            activityCourse: activityCourse,
            description:
              'This journey has already been published, and so cannot be deleted.',
            gradeLevel: gradeLevel
          })
        )
      }
    }

    if (option === 'Edit Details') {
      dispatch(
        showModal('EDIT_JOURNEY_MODAL', {
          apiData,
          activityCourseId,
          journeyId: journey.id
        })
      )
    }
  }

  return (
    <div ref={ref} style={{ boxShadow: 'none' }}>
      <JourneyContainer>
        <FlexContainer>
          <SortIcon />
          <JourneyInfoContainer>
            <H5
              style={{ flex: '30%', color: cleanHexCode(activityCourse.color) }}
              onClick={handleClick}
            >
              <span>{convertToRomanNumerals(index + 1)}. </span>
              {journey.name}
            </H5>
            <FixedAlignmentBox
              justify="flex-end"
              width="80px"
              marginRight="25px"
            >
              <P2
                onClick={handleClick}
              >
                <span>{journey.levels_count} </span>
                Levels
              </P2>
            </FixedAlignmentBox>
            <FixedAlignmentBox
              justify="flex-end"
              width="100px"
              marginRight="90px"
            >
              <P2
                onClick={handleClick}
              >
                <span>{journey.quests_count} </span>
                Activities
              </P2>
            </FixedAlignmentBox>
            <FixedAlignmentBox justify="center" width="86px"
              onClick={handleClick}
            >
              <PublishLabel
                isPublished={!!journey.marked_ready_for_enrollment_at}
              />
            </FixedAlignmentBox>
            <EditOrDeleteMenuButton
              options={
                !!journey.marked_ready_for_enrollment_at
                  ? ['Edit Details']
                  : ['Edit Details', 'Delete']
              }
              handleChosenOption={handleClose}
            />
          </JourneyInfoContainer>
        </FlexContainer>
      </JourneyContainer>
    </div>
  )
}

JourneyRow.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourse: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  journey: PropTypes.object.isRequired,
  selectedAcademicYearId: PropTypes.number.isRequired,
  moveCard: PropTypes.func.isRequired
}

export default withApiData(JourneyRow)



// WEBPACK FOOTER //
// ./src/components/mycourses/Journey/JourneyRow.js